import React, { useCallback, useMemo, useState } from 'react'
import { createRequiredContext } from './create-required-context'

/** Number of typeface shortcuts shown for recently-applied font families. */
const RECENT_FONT_FAMILY_COUNT = 3

export type RecentFontFamiliesValue = {
  /** The most recently *applied* font family ids, most-recent first, deduped, capped at 3. */
  recentFontFamilyIds: readonly number[]
  recordAppliedFontFamily: (fontFamilyId: number) => void
}

const recentFontFamilies = createRequiredContext<RecentFontFamiliesValue>('RecentFontFamilies')

export const useRecentFontFamilies = recentFontFamilies.useRequiredContext

/** Moves `fontFamilyId` to the front of `prev`, deduped and capped at `RECENT_FONT_FAMILY_COUNT`. */
export function pushRecentFontFamilyId(
  prev: readonly number[],
  fontFamilyId: number
): readonly number[] {
  if (prev[0] === fontFamilyId) return prev

  return [fontFamilyId, ...prev.filter((id) => id !== fontFamilyId)].slice(
    0,
    RECENT_FONT_FAMILY_COUNT
  )
}

/**
 * Owns the recent-typeface list above the tool panels. The Type and Select tools each mount their
 * own `TypePanel`, so exiting a text frame swaps panels; keeping the list here (not in panel state)
 * keeps the shortcuts. A separate context from `FontContext` so applying a font doesn't re-render
 * the text measurers that depend on the whole font context object.
 */
export function RecentFontFamiliesProvider({ children }: { children: React.ReactNode }) {
  const [recentFontFamilyIds, setRecentFontFamilyIds] = useState<readonly number[]>([])

  const recordAppliedFontFamily = useCallback((fontFamilyId: number) => {
    setRecentFontFamilyIds((prev) => pushRecentFontFamilyId(prev, fontFamilyId))
  }, [])

  const value = useMemo<RecentFontFamiliesValue>(
    () => ({ recentFontFamilyIds, recordAppliedFontFamily }),
    [recentFontFamilyIds, recordAppliedFontFamily]
  )

  return <recentFontFamilies.Provider value={value}>{children}</recentFontFamilies.Provider>
}
